<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import { useAlert } from '@/libs/alertPortal'
import { captureException } from '@sentry/vue'

import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import type { MemberManagementFormType } from '@/components/organisms/MemberManagementForm/formSchema'
import MemberManagementForm from '@/components/organisms/MemberManagementForm/MemberManagementForm.vue'
import type {
  SettingsPageMemberManagementUpdateContentViewQueryFragment,
  SettingsPageMemberManagementUpdateContentViewStaffFragment,
} from './SettingsPageMemberManagementUpdateContentView.generated'
import { defineResponseHandlers } from '@/libs/utilities/graphql'
import {
  useUpdateStaffMutation,
  type UpdateStaffMutation,
} from './SettingsPageMemberManagementUpdateView.generated'

const props = defineProps<{
  staffGUID: string
  queryFragment: SettingsPageMemberManagementUpdateContentViewQueryFragment
  staffFragment: SettingsPageMemberManagementUpdateContentViewStaffFragment
}>()

const emit = defineEmits<{
  (event: 'updatedStaff'): void
}>()

const router = useRouter()
const alert = useAlert()

const initialForm = computed<MemberManagementFormType>(() => ({
  email: props.staffFragment.email,
  name: props.staffFragment.name ?? undefined,
  role: props.staffFragment.role,
}))

const isSubmitting = ref(false)

const updateStaffMutation = useUpdateStaffMutation()

const handleSubmit = async (form: MemberManagementFormType) => {
  if (isSubmitting.value) {
    return
  }
  isSubmitting.value = true

  const name = form.name === '' ? undefined : form.name

  try {
    const data = await updateStaffMutation.mutate({
      input: {
        guid: props.staffGUID,
        name: name,
        role: form.role,
      },
    })

    if (data?.errors || !data?.data) {
      throw new Error('mutation failed')
    }

    defineResponseHandlers<UpdateStaffMutation['updateStaff'], void>({
      UpdateStaffErrorNotAuthorityPayload: () => {
        // eslint-disable-next-line fp/no-mutating-methods
        router.push({ name: 'errorForbidden' })
      },
      UpdateStaffSuccessPayload: () => {
        alert.success('メンバーを更新しました。')
        emit('updatedStaff')
      },
    })(data.data.updateStaff)

    isSubmitting.value = false
  } catch (e) {
    isSubmitting.value = false
    console.error(e)
    captureException(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  }
}
</script>

<template>
  <SettingsPagePanelHeader>
    <template #title>メンバー編集</template>
  </SettingsPagePanelHeader>
  <MemberManagementForm
    v-if="queryFragment"
    :fragment="queryFragment"
    :is-submitting="isSubmitting"
    :initial-form="initialForm"
    :is-edit-page="true"
    :disable-role="
      queryFragment.planner != undefined &&
      (props.staffFragment.email === queryFragment.planner.email ||
        props.queryFragment.viewer.staff.guid === props.staffFragment.guid)
    "
    @submit="handleSubmit"
  />
</template>
